import React from 'react';
import PropTypes from 'prop-types';
import OmForm from '../../ui/OmForm';
import OmTextField from '../../ui/OmTextField';
import OmDatePicker from '../../ui/OmDatePicker';
import OmButton from '../../ui/OmButton';
import { LoadingButton } from '../../ui/LoadingSpinner';
import BankBranchSelect from '../../ui/BankBranchSelect';

/**
 * Step 2: Claimant Information Component
 * Handles claimant personal details and banking information
 */
const Step2ClaimantInformation = ({
  formData,
  errors,
  validationErrors,
  isLoading,
  onInputChange,
  onFieldBlur,
  onNext,
  onPrevious,
  currentStep,
  schema
}) => {
  // Handle form submission for this step
  const handleStepSubmit = (e) => {
    e.preventDefault();
    if (onNext) {
      onNext();
    }
  };

  // Helper function to render field validation messages
  const renderFieldValidation = (fieldName) => {
    const hasFormError = errors[fieldName];
    const hasValidationError = validationErrors[fieldName];
    
    return (
      <>
        {hasFormError && (
          <p className="form-error">{hasFormError}</p>
        )}
        {hasValidationError && !hasFormError && (
          <p className="form-validation-warning">
            ⚠️ {hasValidationError}
            <span className="text-xs block mt-1 text-gray-500">
              This doesn't prevent submission but may require review.
            </span>
          </p>
        )}
      </>
    );
  };

  // Render loading state if schema is not available
  if (!schema || !schema.baseFields) {
    return (
      <div className="text-center py-12">
        <div className="mb-4">
          <svg className="w-16 h-16 mx-auto text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-steel text-lg">
          {!schema ? 'Schema not loaded yet' : 'Schema structure incomplete'}
        </p>
        <p className="text-steel text-sm mt-2">
          {!schema 
            ? 'Please wait while we load the form schema...' 
            : 'Missing required form field definitions. Please contact technical support.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OmForm
        titleText="Claimant Information"
        descriptionText="Please provide your personal details and banking information for claim processing"
        onSubmit={handleStepSubmit}
        formId="claimant-information-form"
        submitButtonLabel="Continue to Claim Details"
        hideSubmitButton={false}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Claimant Name */}
          <div className="space-y-2">
            <OmTextField
              label="Claimant Name"
              name="claimantName"
              type="text"
              value={formData.claimantName || ''}
              onChange={(e) => onInputChange('claimantName', e.target.value)}
              onBlur={(e) => onFieldBlur && onFieldBlur('claimantName', e.target.value)}
              required={schema.baseFields?.claimantName?.required}
              error={errors.claimantName}
              disabled={isLoading}
              placeholder="Enter full claimant name..."
            />
            {renderFieldValidation('claimantName')}
          </div>

          {/* Mobile Number */}
          <div className="space-y-2">
            <OmTextField
              label="Mobile Number"
              name="claimantMobile"
              type="tel"
              value={formData.claimantMobile || ''}
              onChange={(e) => onInputChange('claimantMobile', e.target.value)}
              error={errors.claimantMobile}
              disabled={isLoading}
              placeholder="e.g. +233242XXXXXXX"
            />
            {errors.claimantMobile && (
              <p className="text-sm text-red-600">{errors.claimantMobile}</p>
            )}
            <p className="text-sm text-gray-600">Include country code (e.g. +233242XXXXXXX)</p>
          </div>

          {/* Email Address */}
          <div className="space-y-2">
            <OmTextField
              label="Email Address"
              name="claimantEmail"
              type="email"
              value={formData.claimantEmail || ''}
              onChange={(e) => onInputChange('claimantEmail', e.target.value)}
              error={errors.claimantEmail}
              disabled={isLoading}
              placeholder="user@example.com"
            />
            {errors.claimantEmail && (
              <p className="text-sm text-red-600">{errors.claimantEmail}</p>
            )}
            <p className="text-sm text-gray-600">Must be a valid email, e.g. user@example.com</p>
          </div>

          {/* Date of Birth */}
          <div className="space-y-2">
            <OmDatePicker
              label="Date of Birth"
              name="claimantDateOfBirth"
              value={formData.claimantDateOfBirth || ''}
              onChange={(e) => onInputChange('claimantDateOfBirth', e.target.value)}
              required={schema.baseFields?.claimantDateOfBirth?.required}
              error={errors.claimantDateOfBirth}
              disabled={isLoading}
            />
            {renderFieldValidation('claimantDateOfBirth')}
          </div>

          {/* Address */}
          <div className="space-y-2 lg:col-span-2">
            <OmTextField
              label="Address"
              name="claimantAddress"
              value={formData.claimantAddress || ''}
              onChange={(e) => onInputChange('claimantAddress', e.target.value)}
              rows={3}
              error={errors.claimantAddress}
              disabled={isLoading}
              placeholder="Enter full address including postal code..."
            />
          </div>

          {/* Bank Details Section */}
          <div className="lg:col-span-2">
            <h4 className="text-lg font-medium text-deep-jungle mb-4 border-b border-woodland-moss/20 pb-2">
              Banking Details
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Bank Account Name */}
              <div className="space-y-2">
                <OmTextField
                  label="Bank Account Name"
                  name="bankAccountName"
                  type="text"
                  value={formData.bankAccountName || ''}
                  onChange={(e) => onInputChange('bankAccountName', e.target.value)}
                  required={schema.baseFields?.bankAccountName?.required}
                  error={errors.bankAccountName}
                  disabled={isLoading}
                  placeholder="Enter account holder name..."
                />
              </div>

              {/* Bank & Branch Selection */}
              <div className="space-y-2">
                <BankBranchSelect
                  label="Bank & Branch"
                  name="bankBranch"
                  value={formData.bankBranch || ''}
                  onChange={(e) => onInputChange('bankBranch', e.target.value)}
                  onBankInfoSelected={(bankInfo) => {
                    // Auto-populate bank name and branch code when user selects
                    onInputChange('bankName', bankInfo.bankName);
                    onInputChange('branch', bankInfo.branchId);
                    onInputChange('bankCode', bankInfo.bankCode);
                  }}
                  required={schema.baseFields?.bankName?.required || schema.baseFields?.branch?.required}
                  error={errors.bankName || errors.branch}
                  disabled={isLoading}
                />
              </div>

              {/* Account Number */}
              <div className="space-y-2">
                <OmTextField
                  label="Account Number"
                  name="accountNumber"
                  type="text"
                  value={formData.accountNumber || ''}
                  onChange={(e) => onInputChange('accountNumber', e.target.value)}
                  required={schema.baseFields?.accountNumber?.required}
                  error={errors.accountNumber}
                  disabled={isLoading}
                  placeholder="Enter account number..."
                />
                {errors.accountNumber && (
                  <p className="text-sm text-red-600">{errors.accountNumber}</p>
                )}
                <p className="text-sm text-gray-600">8–20 digits, numbers only</p>
              </div>

              {/* Hidden fields for backend compatibility */}
              <input type="hidden" name="bankName" value={formData.bankName || ''} />
              <input type="hidden" name="branch" value={formData.branch || ''} />
              <input type="hidden" name="bankCode" value={formData.bankCode || ''} />

            </div>
          </div>
        </div>

        {/* Previous Button in actions slot */}
        <div slot="actions">
          <div 
            style={{
              '--omk-outlined-button-label-text-color': 'var(--color-steel)',
              '--omk-outlined-button-icon-color': 'var(--color-steel)',
              '--omk-outlined-button-outline-color': 'var(--color-silver-mist)',
              '--omk-outlined-button-hover-label-text-color': 'var(--color-deep-jungle)',
              '--omk-outlined-button-hover-icon-color': 'var(--color-deep-jungle)',
              '--omk-outlined-button-hover-state-layer-color': 'var(--color-silver-mist)'
            }}
          >
            <OmButton
              type="button"
              onClick={onPrevious}
              disabled={isLoading}
              buttonType="outlined-button"
              buttonSize="medium"
              leadingIcon="arrow_back"
            >
              Previous
            </OmButton>
          </div>
        </div>
      </OmForm>
    </div>
  );
};

Step2ClaimantInformation.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  validationErrors: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onFieldBlur: PropTypes.func,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  currentStep: PropTypes.number.isRequired,
  schema: PropTypes.object
};

Step2ClaimantInformation.defaultProps = {
  validationErrors: {},
  onFieldBlur: null,
  onPrevious: null,
  schema: null
};

export default Step2ClaimantInformation;